import { ApiError, type Problem } from './api';
import { composeApiRequestUrl } from './config/api-base-url';
import { readJson } from './http';
import {
  NATIVE_CSRF_COOKIE,
  NATIVE_CSRF_HEADER,
} from './native-booking-constants';
import {
  parseNativeBooking,
  parseNativeBookingList,
  parseNativeEditProposal,
  parseNativeSuggestionList,
} from './native-booking-guards';

export interface NativeMe {
  readonly account_id: string;
  readonly email: string;
  readonly expires_at: string;
}

export interface NativeCatalogSite {
  readonly id: string;
  readonly code: string;
  readonly display_name: string;
}

export interface NativeCatalog {
  readonly sites: readonly NativeCatalogSite[];
  readonly equipment_types: readonly {
    readonly id: string;
    readonly code: string;
    readonly display_name: string;
  }[];
}

export interface NativeAvailabilitySlot {
  readonly start_at: string;
  readonly end_at?: string;
  readonly available: boolean;
}

export type NativeResultPdfAvailability =
  | 'AVAILABLE'
  | 'PENDING'
  | 'MISSING'
  | 'NOT_READY';

export interface NativeResultPdf {
  readonly availability: NativeResultPdfAvailability;
  readonly inspection_case_id: string | null;
  readonly case_status: string | null;
  readonly message: string;
}

export interface NativeBooking {
  readonly id: string;
  readonly public_number: string;
  readonly status: string;
  readonly inspection_site_id: string;
  readonly equipment_type_id: string;
  readonly slot_start_at: string;
  readonly slot_end_at: string;
  readonly hold_generation: number;
  readonly hold_expires_at: string | null;
  readonly current_inspection_case_id: string | null;
  readonly draft: Record<string, unknown>;
  readonly version: string;
  readonly booking_source: 'NATIVE';
  readonly editable: boolean;
  readonly case_status: string | null;
  readonly result_pdf?: NativeResultPdf;
}

export interface NativeFormField {
  readonly field_key: string;
  readonly label: string;
  readonly value_type: string;
  readonly required: boolean;
  readonly applicable: boolean;
  readonly validation: Record<string, unknown>;
  readonly semantic_key?: string | null;
  /** Server snapshot path; used with semantic_key ?? snapshot_path ?? field_key for suggestions. */
  readonly snapshot_path?: string | null;
}

export interface NativeFormDocumentRequirement {
  readonly document_type_id: string;
  readonly display_label: string;
  readonly subject_binding: string;
  readonly required: boolean;
  readonly applicable: boolean;
  readonly metadata_fields: readonly {
    readonly field_code: string;
    readonly label: string;
    readonly value_type: string;
    readonly required_rule: Record<string, unknown>;
    readonly validation?: Record<string, unknown>;
  }[];
}

export interface NativeReuseContext {
  readonly resolution_state: string;
  readonly contractor_options: readonly {
    readonly contractor_profile_id: string;
    readonly display_name: string;
  }[];
  readonly selected_contractor_profile_id: string | null;
  readonly vehicle_options: readonly {
    readonly vehicle_profile_id: string;
    readonly display_name: string;
  }[];
  readonly selected_vehicle_profile_id: string | null;
  /** Durable UNSET | NEW | EXISTING — null UUID alone is not enough. */
  readonly contractor_selection_choice?: 'UNSET' | 'NEW' | 'EXISTING';
  readonly vehicle_selection_choice?: 'UNSET' | 'NEW' | 'EXISTING';
}

export interface NativeSuggestion {
  readonly id: string;
  readonly semantic_key: string;
  readonly value: string;
}

export interface NativeEditProposal {
  readonly id: string;
  readonly booking_id: string;
  readonly status: string;
  readonly base_booking_version: string;
  readonly proposal_version: string;
  readonly inspection_site_id: string;
  readonly equipment_type_id: string;
  readonly slot_start_at: string;
  readonly slot_end_at: string;
  readonly hold_generation: number | null;
  readonly hold_expires_at: string | null;
  readonly expires_at: string | null;
  readonly draft: Record<string, unknown>;
}

export interface NativeAdminBlock {
  readonly id: string;
  readonly inspection_site_id: string;
  readonly slot_start_at: string;
  readonly slot_end_at: string;
  readonly reason: string | null;
}

export interface NativeFormResponse {
  readonly booking: NativeBooking;
  readonly configuration: {
    readonly plan_revision_id: string | null;
    readonly plan_revision_number: number | null;
    readonly configuration_fingerprint: string;
    readonly inspection_site_id: string;
    readonly inspection_site_name: string;
    readonly equipment_type_id: string;
    readonly equipment_type_name: string;
    readonly freshness: string;
    readonly refresh_reason?: string | null;
  };
  readonly fields: readonly NativeFormField[];
  readonly document_requirements: readonly NativeFormDocumentRequirement[];
  readonly draft: Record<string, unknown>;
  readonly edit_proposal: NativeEditProposal | null;
  readonly reuse_context: NativeReuseContext | null;
}

export interface NativeAdminSchedule {
  readonly inspection_site_id: string;
  readonly timezone: string;
  readonly version: string;
  readonly configured: boolean;
  readonly slot_duration_minutes?: 30 | 60;
  readonly weekly: readonly {
    readonly weekday: number;
    readonly open: boolean;
    readonly startMinute?: number | null;
    readonly endMinute?: number | null;
    readonly start_minute?: number | null;
    readonly end_minute?: number | null;
  }[];
  readonly breaks: readonly {
    readonly weekday: number;
    readonly start_minute: number;
    readonly end_minute: number;
  }[];
  readonly exceptions: readonly {
    readonly date: string;
    readonly is_closed: boolean;
    readonly start_minute: number | null;
    readonly end_minute: number | null;
  }[];
}

function readBrowserCookie(name: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined;
  }
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) {
      const raw = trimmed.slice(prefix.length);
      try {
        return decodeURIComponent(raw);
      } catch {
        return raw;
      }
    }
  }
  return undefined;
}

function problemFromBody(status: number, body: unknown): ApiError {
  const problem = (typeof body === 'object' && body !== null ? body : {}) as Problem;
  return new ApiError(status, problem);
}

async function nativeRequest(
  baseUrl: string,
  method: string,
  path: string,
  options: {
    body?: unknown;
    csrf?: boolean;
    idempotencyKey?: string;
    adminBearer?: string;
  } = {},
): Promise<{ status: number; ok: boolean; body: unknown }> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.adminBearer !== undefined) {
    headers.Authorization = `Bearer ${options.adminBearer}`;
  }
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (options.csrf === true) {
    const csrf = readBrowserCookie(NATIVE_CSRF_COOKIE);
    if (csrf !== undefined) {
      headers[NATIVE_CSRF_HEADER] = csrf;
    }
  }
  if (options.idempotencyKey !== undefined) {
    headers['Idempotency-Key'] = options.idempotencyKey;
  }
  return readJson(composeApiRequestUrl(baseUrl, path), {
    method,
    headers,
    credentials: options.adminBearer === undefined ? 'include' : 'omit',
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
}

function assertOk(result: { status: number; ok: boolean; body: unknown }): unknown {
  if (!result.ok) {
    throw problemFromBody(result.status, result.body);
  }
  return result.body;
}

function filenameFromContentDisposition(header: string | null): string {
  if (header === null || header.trim() === '') {
    return 'inspection-result.pdf';
  }
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].trim());
    } catch {
      /* fall through */
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header) ?? /filename=([^;]+)/i.exec(header);
  if (plain?.[1]) {
    return plain[1].trim();
  }
  return 'inspection-result.pdf';
}

export function createNativeBookingApi(baseUrl: string) {
  return {
    async fetchAltchaChallenge(): Promise<string> {
      const result = await nativeRequest(baseUrl, 'GET', '/native-auth/altcha-challenge');
      const body = assertOk(result) as { challenge: string };
      return body.challenge;
    },

    async requestOtp(email: string, altcha: string): Promise<void> {
      const result = await nativeRequest(baseUrl, 'POST', '/native-auth/otp/request', {
        body: { email, altcha },
      });
      if (result.status !== 202) {
        throw problemFromBody(result.status, result.body);
      }
    },

    async verifyOtp(email: string, code: string): Promise<NativeMe> {
      const result = await nativeRequest(baseUrl, 'POST', '/native-auth/otp/verify', {
        body: { email, code },
      });
      return assertOk(result) as NativeMe;
    },

    async me(): Promise<NativeMe> {
      const result = await nativeRequest(baseUrl, 'GET', '/native-auth/me');
      return assertOk(result) as NativeMe;
    },

    async logout(): Promise<void> {
      const result = await nativeRequest(baseUrl, 'POST', '/native-auth/logout', { csrf: true });
      if (result.status !== 204 && !result.ok) {
        throw problemFromBody(result.status, result.body);
      }
    },

    async catalog(): Promise<NativeCatalog> {
      const result = await nativeRequest(baseUrl, 'GET', '/native-bookings/catalog');
      return assertOk(result) as NativeCatalog;
    },

    async availability(siteId: string, date: string) {
      const result = await nativeRequest(
        baseUrl,
        'GET',
        `/native-bookings/availability?site_id=${encodeURIComponent(siteId)}&date=${encodeURIComponent(date)}`,
      );
      return assertOk(result) as {
        inspection_site_id: string;
        date: string;
        timezone: string;
        slot_duration_minutes?: 30 | 60;
        slots: readonly NativeAvailabilitySlot[];
      };
    },

    async listBookings(): Promise<readonly NativeBooking[]> {
      const result = await nativeRequest(baseUrl, 'GET', '/native-bookings');
      return parseNativeBookingList(assertOk(result));
    },

    async getBooking(bookingId: string): Promise<NativeBooking> {
      const result = await nativeRequest(baseUrl, 'GET', `/native-bookings/${bookingId}`);
      return parseNativeBooking(assertOk(result));
    },

    /**
     * Downloads the archival signed PDF for a booking owned by the current native session.
     * Uses cookie credentials; returns a Blob for browser save (caller revokes object URLs).
     */
    async downloadSignedPdf(bookingId: string): Promise<{
      readonly blob: Blob;
      readonly filename: string;
    }> {
      const response = await fetch(
        composeApiRequestUrl(baseUrl, `/native-bookings/${encodeURIComponent(bookingId)}/signed-pdf`),
        {
          method: 'GET',
          credentials: 'include',
          headers: { Accept: 'application/pdf' },
        },
      );
      if (!response.ok) {
        let body: unknown = null;
        const contentType = response.headers.get('content-type') ?? '';
        if (contentType.includes('application/json') || contentType.includes('problem+json')) {
          try {
            body = await response.json();
          } catch {
            body = null;
          }
        }
        throw problemFromBody(response.status, body);
      }
      const contentType = response.headers.get('content-type') ?? '';
      if (!contentType.includes('application/pdf')) {
        throw problemFromBody(response.status, {
          code: 'VALIDATION_ERROR',
          title: 'Unexpected response type',
          detail: 'Server did not return a PDF',
        });
      }
      const blob = await response.blob();
      const filename = filenameFromContentDisposition(response.headers.get('content-disposition'));
      return { blob, filename };
    },

    async createHold(input: {
      inspection_site_id: string;
      equipment_type_id: string;
      slot_start_at: string;
      booking_id?: string;
      expected_version?: string;
      idempotencyKey: string;
    }): Promise<NativeBooking> {
      const result = await nativeRequest(baseUrl, 'POST', '/native-bookings/holds', {
        csrf: true,
        idempotencyKey: input.idempotencyKey,
        body: {
          inspection_site_id: input.inspection_site_id,
          equipment_type_id: input.equipment_type_id,
          slot_start_at: input.slot_start_at,
          booking_id: input.booking_id,
          expected_version: input.expected_version,
        },
      });
      return parseNativeBooking(assertOk(result));
    },

    async patchDraft(
      bookingId: string,
      draft: Record<string, unknown>,
      expectedVersion?: string,
    ): Promise<NativeBooking> {
      const result = await nativeRequest(baseUrl, 'PATCH', `/native-bookings/${bookingId}/draft`, {
        csrf: true,
        body: { draft, expected_version: expectedVersion },
      });
      return parseNativeBooking(assertOk(result));
    },

    async reuseOptions(): Promise<NativeReuseContext> {
      const result = await nativeRequest(baseUrl, 'GET', '/native-bookings/reuse-options');
      return assertOk(result) as NativeReuseContext;
    },

    async selectProfiles(
      bookingId: string,
      input: {
        expected_version: string;
        selected_contractor_profile_id: string | null | 'NEW';
        selected_vehicle_profile_id: string | null | 'NEW';
      },
    ): Promise<{ reuse_context: NativeReuseContext; booking_version: string }> {
      const result = await nativeRequest(
        baseUrl,
        'POST',
        `/native-bookings/${bookingId}/select-profiles`,
        {
          csrf: true,
          body: {
            expected_version: input.expected_version,
            selected_contractor_profile_id: input.selected_contractor_profile_id ?? 'NEW',
            selected_vehicle_profile_id: input.selected_vehicle_profile_id ?? 'NEW',
          },
        },
      );
      return assertOk(result) as { reuse_context: NativeReuseContext; booking_version: string };
    },

    async getForm(bookingId: string, proposalId?: string): Promise<NativeFormResponse> {
      const query =
        proposalId === undefined
          ? ''
          : `?proposal_id=${encodeURIComponent(proposalId)}`;
      const result = await nativeRequest(
        baseUrl,
        'GET',
        `/native-bookings/${bookingId}/form${query}`,
      );
      const body = assertOk(result) as NativeFormResponse;
      if (body.edit_proposal !== null && body.edit_proposal !== undefined) {
        return {
          ...body,
          edit_proposal: parseNativeEditProposal(body.edit_proposal),
        };
      }
      return { ...body, edit_proposal: null };
    },

    async submit(
      bookingId: string,
      input: {
        expected_version: string;
        idempotencyKey: string;
        configuration_plan_revision_id?: string | null;
        configuration_fingerprint?: string;
      },
    ): Promise<NativeBooking> {
      const result = await nativeRequest(baseUrl, 'POST', `/native-bookings/${bookingId}/submit`, {
        csrf: true,
        idempotencyKey: input.idempotencyKey,
        body: {
          expected_version: input.expected_version,
          configuration_plan_revision_id: input.configuration_plan_revision_id ?? undefined,
          configuration_fingerprint: input.configuration_fingerprint,
        },
      });
      return parseNativeBooking(assertOk(result));
    },

    async cancel(
      bookingId: string,
      input: { expected_version: string; expected_case_id: string; idempotencyKey: string },
    ): Promise<NativeBooking> {
      const result = await nativeRequest(baseUrl, 'POST', `/native-bookings/${bookingId}/cancel`, {
        csrf: true,
        idempotencyKey: input.idempotencyKey,
        body: {
          expected_version: input.expected_version,
          expected_case_id: input.expected_case_id,
        },
      });
      return parseNativeBooking(assertOk(result));
    },

    async openEditProposal(
      bookingId: string,
      input: { expected_version: string; expected_case_id: string; idempotencyKey: string },
    ): Promise<{ edit_proposal: NativeEditProposal }> {
      const result = await nativeRequest(
        baseUrl,
        'POST',
        `/native-bookings/${bookingId}/edit-proposals`,
        {
          csrf: true,
          idempotencyKey: input.idempotencyKey,
          body: {
            expected_version: input.expected_version,
            expected_case_id: input.expected_case_id,
          },
        },
      );
      const body = assertOk(result) as { edit_proposal: unknown };
      return { edit_proposal: parseNativeEditProposal(body.edit_proposal) };
    },

    async patchEditProposal(
      bookingId: string,
      proposalId: string,
      input: {
        expected_proposal_version: string;
        inspection_site_id?: string;
        equipment_type_id?: string;
        slot_start_at?: string;
        draft?: Record<string, unknown>;
      },
    ): Promise<{ booking: NativeBooking; edit_proposal: NativeEditProposal }> {
      const result = await nativeRequest(
        baseUrl,
        'PATCH',
        `/native-bookings/${bookingId}/edit-proposals/${proposalId}`,
        {
          csrf: true,
          body: input,
        },
      );
      const body = assertOk(result) as { booking: unknown; edit_proposal: unknown };
      return {
        booking: parseNativeBooking(body.booking),
        edit_proposal: parseNativeEditProposal(body.edit_proposal),
      };
    },

    async applyEditProposal(
      bookingId: string,
      proposalId: string,
      input: {
        expected_version: string;
        expected_case_id: string;
        expected_proposal_version: string;
        idempotencyKey: string;
        configuration_plan_revision_id?: string | null;
        configuration_fingerprint?: string;
      },
    ): Promise<NativeBooking> {
      const result = await nativeRequest(
        baseUrl,
        'POST',
        `/native-bookings/${bookingId}/edit-proposals/${proposalId}/apply`,
        {
          csrf: true,
          idempotencyKey: input.idempotencyKey,
          body: {
            expected_version: input.expected_version,
            expected_case_id: input.expected_case_id,
            expected_proposal_version: input.expected_proposal_version,
            configuration_plan_revision_id: input.configuration_plan_revision_id ?? undefined,
            configuration_fingerprint: input.configuration_fingerprint,
          },
        },
      );
      const body = assertOk(result) as { booking?: unknown } | unknown;
      if (
        typeof body === 'object' &&
        body !== null &&
        'booking' in body &&
        (body as { booking: unknown }).booking !== undefined
      ) {
        return parseNativeBooking((body as { booking: unknown }).booking);
      }
      return parseNativeBooking(body);
    },

    async abandonEditProposal(bookingId: string, proposalId: string): Promise<void> {
      const result = await nativeRequest(
        baseUrl,
        'DELETE',
        `/native-bookings/${bookingId}/edit-proposals/${proposalId}`,
        { csrf: true },
      );
      if (result.status !== 204 && !result.ok) {
        throw problemFromBody(result.status, result.body);
      }
    },

    async listSuggestions(semanticKey: string, query: string): Promise<readonly NativeSuggestion[]> {
      const result = await nativeRequest(
        baseUrl,
        'GET',
        `/native-bookings/suggestions?semantic_key=${encodeURIComponent(semanticKey)}&q=${encodeURIComponent(query)}&limit=8`,
      );
      return parseNativeSuggestionList(assertOk(result));
    },

    async hideSuggestion(suggestionId: string): Promise<void> {
      const result = await nativeRequest(
        baseUrl,
        'DELETE',
        `/native-bookings/suggestions?suggestion_id=${encodeURIComponent(suggestionId)}`,
        { csrf: true },
      );
      if (result.status !== 204 && !result.ok) {
        throw problemFromBody(result.status, result.body);
      }
    },
  };
}

export type NativeBookingApi = ReturnType<typeof createNativeBookingApi>;

export function createNativeAdminScheduleApi(baseUrl: string, accessToken: string) {
  const bearer = accessToken;
  async function adminRequest(method: string, path: string, body?: unknown) {
    const result = await nativeRequest(baseUrl, method, path, {
      adminBearer: bearer,
      body,
    });
    return result;
  }

  return {
    async getSchedule(siteId: string): Promise<NativeAdminSchedule> {
      const result = await adminRequest('GET', `/admin/native-schedules/${siteId}`);
      return assertOk(result) as NativeAdminSchedule;
    },

    async putSchedule(
      siteId: string,
      body: {
        expected_version: string;
        weekly: readonly {
          weekday: number;
          open: boolean;
          start_minute: number | null;
          end_minute: number | null;
        }[];
        breaks: readonly {
          weekday: number;
          start_minute: number;
          end_minute: number;
        }[];
        exceptions: readonly {
          date: string;
          is_closed: boolean;
          start_minute: number | null;
          end_minute: number | null;
        }[];
        slot_duration_minutes?: 30 | 60;
      },
    ): Promise<NativeAdminSchedule> {
      const result = await adminRequest('PUT', `/admin/native-schedules/${siteId}`, body);
      return assertOk(result) as NativeAdminSchedule;
    },

    async listBlocks(
      siteId: string,
      fromDate: string,
      toDate: string,
    ): Promise<readonly NativeAdminBlock[]> {
      const result = await adminRequest(
        'GET',
        `/admin/native-schedules/${siteId}/blocks?from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}`,
      );
      const body = assertOk(result) as { items?: unknown };
      if (!Array.isArray(body.items)) {
        throw new Error('Invalid blocks list: expected { items: [...] }');
      }
      return body.items.map((row) => {
        const item = row as NativeAdminBlock;
        return {
          id: item.id,
          inspection_site_id: item.inspection_site_id,
          slot_start_at: item.slot_start_at,
          slot_end_at: item.slot_end_at,
          reason: item.reason ?? null,
        };
      });
    },

    async addBlock(siteId: string, slotStartAt: string, reason: string | null): Promise<NativeAdminBlock> {
      const result = await adminRequest('POST', `/admin/native-schedules/${siteId}/blocks`, {
        slot_start_at: slotStartAt,
        reason,
      });
      return assertOk(result) as NativeAdminBlock;
    },

    async deleteBlock(siteId: string, blockId: string): Promise<void> {
      const result = await adminRequest('DELETE', `/admin/native-schedules/${siteId}/blocks/${blockId}`);
      if (result.status !== 204 && !result.ok) {
        throw problemFromBody(result.status, result.body);
      }
    },
  };
}
